import axios from "axios";
import { API_URL } from "./baseUrl";
import { resolveLoginOtpPhone } from "./trustedBrowser";

export const PHONE_2FA_PROMPT_SESSION_KEY = "finlotax_2fa_phone_prompt_shown";

export function clearPhone2faPromptSession() {
  try {
    sessionStorage.removeItem(PHONE_2FA_PROMPT_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

export function markPhone2faPromptShown() {
  try {
    sessionStorage.setItem(PHONE_2FA_PROMPT_SESSION_KEY, "1");
  } catch {
    /* ignore */
  }
}

export function hasPhone2faPromptBeenShown() {
  try {
    return sessionStorage.getItem(PHONE_2FA_PROMPT_SESSION_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Staff vs client helpers for login 2FA.
 * Staff SMS OTP → personal_phone; client SMS OTP → mfa_phone.
 */
export function isLoginStaffUser(loginUser = {}) {
  const user = loginUser || {};
  const role = String(
    user.user_type || user.role || user.user_role || "",
  )
    .trim()
    .toLowerCase();
  const staffRoles = new Set(["employee", "manager", "admin"]);
  return (
    staffRoles.has(role) ||
    (!!user.employee_id && role !== "client" && !user.client_id)
  );
}

/**
 * Phone required for the post-login MFA prompt.
 * Staff → personal_phone only; client → mfa_phone only (not main phone).
 */
export function resolveRequired2faPhone(loginUser = {}) {
  const staff = isLoginStaffUser(loginUser);
  if (staff) {
    return (
      loginUser.personal_phone ||
      loginUser.personalPhone ||
      loginUser.personal_mobile ||
      null
    );
  }

  return (
    loginUser.mfa_phone ||
    loginUser.mfaPhone ||
    loginUser.otp_phone ||
    loginUser.otpPhone ||
    null
  );
}

/**
 * Resolve dedicated 2FA phone from API responses only (profile + 2FA pref).
 * Do not read phone values from localStorage USER_DATA.
 */
export function resolveRequired2faPhoneFromApi(
  profile = {},
  pref = {},
  staff = isLoginStaffUser(profile),
) {
  if (staff) {
    return (
      profile.personal_phone ||
      profile.personalPhone ||
      profile.personal_mobile ||
      pref.personal_phone ||
      pref.personalPhone ||
      null
    );
  }

  return (
    profile.mfa_phone ||
    profile.mfaPhone ||
    profile.otp_phone ||
    profile.otpPhone ||
    pref.mfa_phone ||
    pref.mfaPhone ||
    pref.otp_phone ||
    pref.otpPhone ||
    null
  );
}

/** Merge identity from storage with API profile; phone fields come from API only. */
export function buildUserDataFromApiProfile(localUser = {}, apiProfile = {}, pref = {}) {
  const staff = isLoginStaffUser({ ...localUser, ...apiProfile });
  const next = {
    ...localUser,
    ...(apiProfile && typeof apiProfile === "object" ? apiProfile : {}),
  };

  if (staff) {
    const personalPhone = resolveRequired2faPhoneFromApi(
      apiProfile,
      pref,
      true,
    );
    next.personal_phone = personalPhone;
    next.personalPhone = personalPhone;
  } else {
    const mfaPhone = resolveRequired2faPhoneFromApi(apiProfile, pref, false);
    next.mfa_phone = mfaPhone;
    next.mfaPhone = mfaPhone;
  }

  return next;
}

/**
 * Persist the 2FA phone field after password auth (before OTP).
 * Staff → personal_phone; client → mfa_phone.
 */
export async function updateLoginPhoneFor2FA({
  email,
  phone,
  loginUser = {},
}) {
  const staff = isLoginStaffUser(loginUser);
  const challengeToken =
    loginUser.login_challenge_token ||
    loginUser.loginChallengeToken ||
    loginUser.phone_update_token ||
    null;

  const dedicatedBody = {
    email: email || loginUser.email,
    ...(staff
      ? {
          personal_phone: phone,
          employee_id: loginUser.employee_id,
        }
      : {
          mfa_phone: phone,
          client_id: loginUser.client_id,
        }),
    ...(challengeToken ? { login_challenge_token: challengeToken } : {}),
  };

  try {
    const response = await axios.post(
      `${API_URL}auth/update-phone-for-2fa`,
      dedicatedBody,
      { withCredentials: true },
    );

    if (response.status === 200 && response.data.status === 1) {
      return {
        success: true,
        phone,
        field: staff ? "personal_phone" : "mfa_phone",
        data: response.data.data,
        message: response.data.message,
      };
    }

    if (response?.data?.message && !loginUser.accessToken) {
      return {
        success: false,
        message: response.data.message || "Failed to save phone number",
      };
    }
  } catch (error) {
    const status = error?.response?.status;
    if (status && status !== 404 && !loginUser.accessToken) {
      return {
        success: false,
        message:
          error?.response?.data?.message ||
          "Failed to save phone number. Please try again.",
      };
    }
  }

  const token = loginUser.accessToken;
  if (!token) {
    return {
      success: false,
      message:
        "Unable to save phone before verification. Backend must support auth/update-phone-for-2fa.",
    };
  }

  const userId = staff ? loginUser.employee_id : loginUser.client_id;
  const path = staff
    ? `employee/updateEmployeeById/${userId}`
    : `client/updateClientById/${userId}`;
  const params = staff ? { personal_phone: phone } : { mfa_phone: phone };

  try {
    const response = await axios.post(`${API_URL}${path}`, params, {
      withCredentials: true,
      headers: {
        "x-access-token": token,
        "user-type": staff ? "employee" : "client",
        "user-id": String(userId ?? ""),
      },
    });

    if (response.status === 200 && response.data.status === 1) {
      return {
        success: true,
        phone,
        field: staff ? "personal_phone" : "mfa_phone",
        data: response.data.data,
        message: response.data.message,
      };
    }

    return {
      success: false,
      message: response.data?.message || "Failed to save phone number",
    };
  } catch (error) {
    return {
      success: false,
      message:
        error?.response?.data?.message ||
        "Failed to save phone number. Please try again.",
    };
  }
}

export function applyPhoneToLoginUser(loginUser, phone) {
  const staff = isLoginStaffUser(loginUser);
  const next = { ...(loginUser || {}) };
  if (!phone) return next;
  if (staff) {
    next.personal_phone = phone;
    next.personalPhone = phone;
  } else {
    next.mfa_phone = phone;
    next.mfaPhone = phone;
  }
  return next;
}

export { resolveLoginOtpPhone };
